/**
 * W3-M3 — semver parse / compare / compatibility-matrix helpers (Bundle 3 M3).
 *
 * Spec: 02_specs/14_m3_iframe_bridge_version_capability_negotiation_2026-05-31.md §5.2 + §5.3.
 *
 * Bundle protocol versions are `major.minor.patch`. The L2 envelope's
 * `bridge_protocol_version` field stays at '1.0' (envelope schema version).
 * THIS module operates on the M3 IMPLEMENTATION version carried in the
 * handshake payload — currently '2.0.0' for the shell.
 *
 * Compatibility rule (spec §5.2):
 *   major equal           → compat baseline (caps decide further)
 *   major different       → BLOCK (forward-incompat by design at major boundary)
 *   minor different,
 *     major equal         → DEGRADE / WARN per capability set
 *   patch different       → ALLOW
 */

export interface SemverParts {
  major: number;
  minor: number;
  patch: number;
}

export type VersionCompareOutcome =
  | 'equal'
  | 'patch_diff'
  | 'minor_diff_app_newer'
  | 'minor_diff_shell_newer'
  | 'major_mismatch';

export type HandshakeBaselineOutcome =
  | 'ALLOW'
  | 'DEGRADE'
  | 'WARN'
  | 'BLOCK';

/** SemVer regex — strict `MAJOR.MINOR.PATCH` (numeric, no pre-release / metadata).
 *  Anchored. Rejects extra components. */
const SEMVER_REGEX = /^(\d+)\.(\d+)\.(\d+)$/;

export function parseSemver(s: string): SemverParts | null {
  if (typeof s !== 'string') return null;
  const m = SEMVER_REGEX.exec(s);
  if (m === null) return null;
  const [, mj, mn, pt] = m;
  const major = Number(mj);
  const minor = Number(mn);
  const patch = Number(pt);
  if (!Number.isFinite(major) || !Number.isFinite(minor) || !Number.isFinite(patch)) {
    return null;
  }
  return { major, minor, patch };
}

export function isValidSemver(s: string): boolean {
  return parseSemver(s) !== null;
}

/** Stable lexicographic-by-component comparator: -1 / 0 / +1. */
export function compareSemver(a: SemverParts, b: SemverParts): -1 | 0 | 1 {
  if (a.major !== b.major) return a.major < b.major ? -1 : 1;
  if (a.minor !== b.minor) return a.minor < b.minor ? -1 : 1;
  if (a.patch !== b.patch) return a.patch < b.patch ? -1 : 1;
  return 0;
}

/** Compute the compare-outcome between the shell version and the app version. */
export function compareVersionOutcome(
  shell: SemverParts,
  app: SemverParts,
): VersionCompareOutcome {
  if (shell.major !== app.major) return 'major_mismatch';
  if (shell.minor === app.minor && shell.patch === app.patch) return 'equal';
  if (shell.minor === app.minor) return 'patch_diff';
  return shell.minor < app.minor ? 'minor_diff_app_newer' : 'minor_diff_shell_newer';
}

/**
 * Baseline outcome from version alone — caller layers capability-set rules on top.
 * Returns one of:
 *   ALLOW   — versions identical, patch-diff, or shell older minor (shell ignores unknown caps)
 *   DEGRADE — shell newer minor (some shell features may lack app capability)
 *   BLOCK   — major mismatch
 *
 * WARN is only reachable when a hard-required capability is missing under a DEGRADE
 * version-baseline; handshake.ts applies that final mapping.
 */
export function versionBaselineOutcome(
  shellSemver: SemverParts,
  appSemver: SemverParts,
): HandshakeBaselineOutcome {
  const cmp = compareVersionOutcome(shellSemver, appSemver);
  switch (cmp) {
    case 'major_mismatch':
      return 'BLOCK';
    case 'equal':
    case 'patch_diff':
    case 'minor_diff_app_newer':
      return 'ALLOW';
    case 'minor_diff_shell_newer':
      return 'DEGRADE';
  }
}
